import { useState } from "react";
import { PlayIcon } from "lucide-react";

export interface VideoEmbedProps {
  /** YouTube か Cloudflare Stream の URL / ID */
  src: string;
  /** 見出しとして出す説明。読み上げの alt にも使う */
  title?: string;
  /** サムネイルを差し替えたいとき */
  poster?: string;
  /**
   * 画面の最初に見えるところに置くとき。台紙を後回しにせず先に読む。
   *
   * 記事の頭に置くと台紙がその画面で一番大きい絵になる。既定の
   * 遅延読み込みのままだと、表示までの時間がそのぶん遅く出る。
   */
  priority?: boolean;
  /** 再生ボタンが押されたとき。1回の表示で1度だけ呼ぶ */
  onPlay?: () => void;
}

type Resolved = {
  kind: "iframe" | "file";
  embedUrl: string;
  poster: string | null;
  label: string;
  /** 置き場の見るページ。JS が無いときに台紙から飛ぶ先 */
  pageUrl: string;
};

const YOUTUBE_ID = /(?:youtube\.com\/(?:watch\?v=|embed\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
const STREAM_URL = /^https:\/\/(customer-[a-z0-9]+\.cloudflarestream\.com)\/([a-f0-9]{32})/i;
const VIMEO_URL =
  /^https:\/\/(?:www\.|player\.)?vimeo\.com\/(?:video\/)?(\d{6,})(?:[/?#]|$)/i;

/**
 * 動画の埋め込み先を判別する。
 *
 * 置き場所は用途で分けている ([[project_lesson_video_hosting_decision]])。
 * - 長い講義や集客用は YouTube。配信がタダで、検索から人が来る
 * - 教材に埋める短い解説は Cloudflare Stream。受講生ごとに絞れる
 * - Vimeo は LP に前から載っている取材動画のぶんだけ。新しく置く先には選ばない
 */
function resolve(src: string): Resolved | null {
  const value = (src || "").trim();
  if (!value) return null;

  const stream = STREAM_URL.exec(value);
  if (stream) {
    const [, host, uid] = stream;
    return {
      kind: "iframe",
      embedUrl: `https://${host}/${uid}/iframe`,
      poster: `https://${host}/${uid}/thumbnails/thumbnail.jpg`,
      label: "Cloudflare Stream",
      pageUrl: `https://${host}/${uid}/watch`,
    };
  }

  const vimeo = VIMEO_URL.exec(value);
  if (vimeo) {
    return {
      kind: "iframe",
      embedUrl: `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`,
      // 自動サムネイルは本人が喋っている途中の一コマになる。
      // 呼ぶ側から poster を渡してもらう
      poster: null,
      label: "Vimeo",
      pageUrl: `https://vimeo.com/${vimeo[1]}`,
    };
  }

  const youtube = YOUTUBE_ID.exec(value);
  const id = youtube ? youtube[1] : /^[A-Za-z0-9_-]{11}$/.test(value) ? value : null;
  if (id) {
    return {
      kind: "iframe",
      // rel=0 で他人のチャンネルの関連動画を出さない。
      // modestbranding は 2023 に廃止されたので付けていない
      embedUrl: `https://www.youtube-nocookie.com/embed/${id}?rel=0&autoplay=1`,
      poster: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
      label: "YouTube",
      pageUrl: `https://www.youtube.com/watch?v=${id}`,
    };
  }

  // R2 などに直接置いた mp4。1本だけ置きたいときや、
  // Stream をまだ有効にしていないときの逃げ道
  if (/\.(mp4|webm|mov)(\?|$)/i.test(value)) {
    return { kind: "file", embedUrl: value, poster: null, label: "動画", pageUrl: value };
  }

  return null;
}

/**
 * 押されるまで本物のプレイヤーを読まない埋め込み。
 *
 * ページ表示と同時に iframe を読むと、動画を1本も見ていないのに
 * ページが重くなる。Stream は「プリロードとバッファリングも
 * 課金対象の配信」として数えるので、料金にも直接効く。
 */
export function VideoEmbed({
  src,
  title,
  poster,
  priority = false,
  onPlay,
}: VideoEmbedProps) {
  const [playing, setPlaying] = useState(false);

  /* 別の動画に変わったら再生前へ戻す。
     記事から記事へ移ったとき、この位置の要素はそのまま使い回されるので、
     playing が true のまま残る。すると押していないのに次の記事の動画が
     いきなり流れ出す (autoplay=1 を付けてあるため) */
  const [lastSrc, setLastSrc] = useState(src);
  if (lastSrc !== src) {
    setLastSrc(src);
    setPlaying(false);
  }

  const resolved = resolve(src);

  if (!resolved) {
    return (
      <p className="my-6 rounded-lg border border-dashed border-main bg-second p-4 max-sm:text-[0.9375rem] text-sm text-sub">
        動画の URL を読み取れませんでした（{src}）
      </p>
    );
  }

  const thumbnail = poster ?? resolved.poster;

  return (
    <figure className="my-6">
      <div className="relative overflow-hidden rounded-xl border border-main bg-black aspect-video">
        {playing && resolved.kind === "file" ? (
          <video
            src={resolved.embedUrl}
            poster={thumbnail ?? undefined}
            controls
            autoPlay
            playsInline
            preload="metadata"
            className="absolute inset-0 h-full w-full"
          />
        ) : playing ? (
          <iframe
            src={resolved.embedUrl}
            title={title ?? resolved.label}
            /* accelerometer と gyroscope は 360 度動画が端末の傾きを読むための
               もの。ここで出すのは講義と取材だけなので渡さない */
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            loading="lazy"
            className="absolute inset-0 h-full w-full border-0"
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setPlaying(true);
              onPlay?.();
            }}
            /* 日本語なので「動画 を再生」と空けない */
            aria-label={title ? `${title}を再生` : "動画を再生"}
            /* 書き出しの HTML (lab-astro) は React を載せないので、この釦に onClick が付かない。
               apps/lab-astro/src/client/video.ts がこの 3 つを読んで、同じ iframe か video に差し替える */
            data-video-embed={resolved.embedUrl}
            data-video-kind={resolved.kind}
            data-video-title={title ?? resolved.label}
            /* 枠が overflow-hidden なので、焦点の輪は内側に描く */
            className="group absolute inset-0 h-full w-full cursor-pointer border-0 p-0 focus-visible:outline focus-visible:outline-[3px] focus-visible:-outline-offset-[3px] focus-visible:outline-[#037f40]"
          >
            {thumbnail && (
              <img
                src={thumbnail}
                alt=""
                loading={priority ? "eager" : "lazy"}
                fetchPriority={priority ? "high" : undefined}
                decoding="async"
                className="h-full w-full object-cover"
              />
            )}
            {/* 台紙の上に黒を重ねない (灰色にくすむ)。再生ボタンも真ん中に置かず左下へ寄せる。
                台紙の題字は真ん中から左に載ることが多く、中央のボタンが字を隠していた */}
            <span className="pointer-events-none absolute bottom-3 left-3 flex h-12 w-12 items-center justify-center rounded-full border border-black/10 bg-white shadow-md transition-transform group-hover:scale-110 sm:bottom-4 sm:left-4 sm:h-14 sm:w-14">
              <PlayIcon className="ml-0.5 h-5 w-5 text-[#037f40] sm:h-6 sm:w-6" fill="currentColor" />
            </span>
          </button>
        )}
      </div>
      {title && (
        <figcaption className="mt-2 font-noto max-sm:text-[0.9375rem] text-sm text-sub">{title}</figcaption>
      )}
      {/* JS が切られていると釦は何もしない。置き場の見るページへの道を残す */}
      <noscript>
        <a
          href={resolved.pageUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-1 inline-block font-noto max-sm:text-[0.9375rem] text-sm font-bold text-[#037f40] underline"
        >
          {resolved.label === "動画" ? "動画を開く" : `${resolved.label}で開く`}
        </a>
      </noscript>
    </figure>
  );
}
